import { useCallback, useEffect, useRef, useState } from "react";
import { useCart } from "@/context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

async function verify(id, isSubscription) {
  const endpoint = isSubscription ? `paypal/subscription/activate/${id}` : `paypal/capture/${id}`;
  const res = await fetch(`${API}/api/payments/${endpoint}`, { method: "POST" });
  if (!res.ok) return res.status >= 500 ? "timeout" : "failed";
  const data = await res.json();
  return data.payment_status === "paid" || data.status === "active" ? "paid" : "timeout";
}

export default function usePaymentVerification(id, isSubscription) {
  const [state, setState] = useState(id ? "checking" : "error");
  const { clearCart } = useCart();
  const autoVerified = useRef(null);
  const inFlight = useRef(null);
  const key = `${isSubscription ? "subscription" : "order"}:${id}`;
  const retry = useCallback(() => {
    if (!id) return;
    if (inFlight.current?.key === key) return inFlight.current.promise;
    setState("checking");
    const promise = verify(id, isSubscription)
      .catch(() => "timeout")
      .then((result) => {
        if (autoVerified.current !== key) return;
        if (result === "paid" && !isSubscription) clearCart();
        setState(result);
      }).finally(() => {
        if (inFlight.current?.key === key) inFlight.current = null;
      });
    inFlight.current = { key, promise };
    return promise;
  }, [id, isSubscription, clearCart, key]);

  useEffect(() => {
    if (autoVerified.current === key) return;
    autoVerified.current = key;
    if (!id) { setState("error"); return; }
    retry();
  }, [retry, key, id]);

  return { state, retry };
}
